import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Check, Shield, Calendar, Building2 } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LabReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    trackPageView('/lab-reports', 'Lab Test Reports');
    fetch(`${BACKEND_URL}/api/lab-reports`).then(r => r.json()).then(data => { setReports(data); if (data.length) setSelected(data[0]); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="lab-reports-page">
      <SEOHead
        title="Lab Reports"
        description="Third-party verified lab test results for all Avi Cement products. Compressive strength, adhesion, density tests from Gujarat Test House and NCCBM."
        path="/lab-reports"
      />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto text-center mb-12">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Quality Assurance</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Lab Test <span className="text-[#F5A623]">Reports</span>
        </h1>
        <p className="text-gray-400 text-sm max-w-lg mx-auto">Har batch test hota hai — IS standards ke against verified results dekhein. Transparency mein humara vishwas hai.</p>
      </motion.div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Cards */}
        <div className="space-y-3 lg:col-span-1">
          {reports.map((r, idx) => (
            <motion.button key={r.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.08 }}
              onClick={() => setSelected(r)} data-testid={`lab-report-card-${r.id}`}
              className={`w-full text-left p-4 rounded-xl border transition-all ${selected?.id === r.id ? 'bg-[#E85A1C]/10 border-[#E85A1C]/30' : 'bg-[#111113] border-gray-800 hover:border-gray-700'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${r.status === 'Pass' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                  {r.status === 'Pass' ? <Check className="w-5 h-5 text-emerald-400" /> : <FlaskConical className="w-5 h-5 text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{r.product}</p>
                  <p className="text-gray-500 text-xs">{r.type}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Report Detail */}
        {selected && (
          <motion.div key={selected.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-[#111113] border border-gray-800 rounded-2xl p-6 md:p-8" data-testid="lab-report-detail">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">{selected.product}</h2>
                <p className="text-gray-400 text-sm">{selected.type}</p>
              </div>
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${selected.status === 'Pass' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                {selected.status}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#0a0a0b] rounded-xl p-4">
                <Shield className="w-4 h-4 text-[#E85A1C] mb-2" />
                <p className="text-gray-500 text-xs">Standard</p>
                <p className="text-white text-sm font-medium">{selected.standard}</p>
              </div>
              <div className="bg-[#0a0a0b] rounded-xl p-4">
                <Building2 className="w-4 h-4 text-[#F5A623] mb-2" />
                <p className="text-gray-500 text-xs">Lab</p>
                <p className="text-white text-sm font-medium">{selected.lab}</p>
              </div>
              <div className="bg-[#0a0a0b] rounded-xl p-4">
                <Calendar className="w-4 h-4 text-emerald-400 mb-2" />
                <p className="text-gray-500 text-xs">Test Date</p>
                <p className="text-white text-sm font-medium">{new Date(selected.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
            </div>

            <div className="bg-[#0a0a0b] rounded-xl p-4 mb-4">
              <p className="text-gray-500 text-xs mb-1">Result</p>
              <p className="text-[#F5A623] text-lg font-bold">{selected.result}</p>
            </div>

            {selected.details && (
              <div>
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Detailed Results</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(selected.details).map(([key, val]) => (
                    <div key={key} className="flex justify-between items-center bg-[#0a0a0b] rounded-lg px-4 py-2.5">
                      <span className="text-gray-400 text-xs capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-white text-sm font-medium">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LabReportsPage;
